import { useCallback, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useSaveCardOrder } from '../api/mutations';
import { realtimeKeys } from '../api/queries';
import { changedPositions, moveWithinGroup, normalizeRecords } from '../lib/cardOrder';

/**
 * Drag-and-drop reordering for the Realtime grid. `moveCard` reorders the cached
 * records as the card is dragged; `commitOrder` saves the positions that changed,
 * once, when it's dropped.
 */
function useCardReorder(archived: boolean, groupId: string | null) {
    const queryClient = useQueryClient();
    const { mutate: saveOrder } = useSaveCardOrder();
    // Stored orders when the drag started, and the order it has reached so far.
    const storedAtStart = useRef<Map<string, unknown> | null>(null);
    const pendingOrder = useRef<Map<string, number> | null>(null);

    // Read from the cache, not the last render: several hovers can fire between renders.
    const currentCards = useCallback(() => {
        const records = queryClient.getQueryData<any[]>(realtimeKeys.list(archived)) ?? [];
        return normalizeRecords(
            records.map((record) => ({
                deviceId: record.device_id as string,
                order: record.order ?? null,
                storedOrder: record.order,
                groupIds: (record.group_ids || []) as string[],
            }))
        );
    }, [queryClient, archived]);

    const applyOrder = useCallback(
        (nextOrder: Map<string, number>) => {
            const queryKey = realtimeKeys.list(archived);
            // A poll landing mid-drag would put the old order back.
            queryClient.cancelQueries({ queryKey });
            queryClient.setQueryData<any[]>(queryKey, (records) =>
                records?.map((record) => ({ ...record, order: nextOrder.get(record.device_id) ?? record.order }))
            );
        },
        [archived, queryClient]
    );

    const moveCard = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            const cards = currentCards();
            const members = groupId
                ? new Set(cards.filter((card) => card.groupIds.includes(groupId)).map((card) => card.deviceId))
                : null;
            const nextIds = moveWithinGroup(
                cards.map((card) => card.deviceId),
                members,
                dragIndex,
                hoverIndex
            );
            if (!nextIds) return;

            storedAtStart.current ??= new Map(cards.map((card) => [card.deviceId, card.storedOrder]));
            pendingOrder.current = new Map(nextIds.map((id, index) => [id, index + 1]));
            applyOrder(pendingOrder.current);
        },
        [applyOrder, currentCards, groupId]
    );

    const commitOrder = useCallback(() => {
        const stored = storedAtStart.current;
        const next = pendingOrder.current;
        storedAtStart.current = null;
        pendingOrder.current = null;
        if (!stored || !next) return;

        applyOrder(next);
        const changes = changedPositions(stored, next);
        if (changes.length) saveOrder(changes);
    }, [applyOrder, saveOrder]);

    // The tab closed mid-drag, before the drop could save it.
    useEffect(() => {
        window.addEventListener('pagehide', commitOrder);
        return () => window.removeEventListener('pagehide', commitOrder);
    }, [commitOrder]);

    return { moveCard, commitOrder };
}

export default useCardReorder;
